import { NonAppNavWrapper } from '@/components/generic/non-app-nav'
import ToolLinks from '@/components/page-specific/tools/tool-links'
import { useAuth, useUser } from '@clerk/nextjs'
import { ArrowRight, Check } from 'lucide-react'
import { NextSeo } from 'next-seo'
import { Inter } from 'next/font/google'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'

const inter = Inter({ subsets: ['latin'] })

export default function SentenceRewriter() {
  const router = useRouter()
  const { isSignedIn } = useAuth()

  const { user } = useUser()
  const isSubscribed = user?.publicMetadata.isSubscribed

  return (
    <>
      <NextSeo
        title="v0 report - Your Ultimate AI Sentence Rewriter Tool"
        description="v0 Report enhances your writing effortlessly with our advanced AI Sentence Rewriter tool. Enhance the quality of any sentence in one click."
      />
      <NonAppNavWrapper>
        <main className={`scroll-smooth flex min-h-screen flex-col ${inter.className}`}>
          <div className=" mt-36">
            <div className="mx-auto text-center">
              <h1 className="text-6xl text-zinc-700  grid gap-2">
                <span className="font-medium">v0 Report - Your Premier AI </span>
                <span className="font-thin italic font-serif leading-none text-yellow-600">
                  Sentence Rewriter for Academic Success
                </span>
              </h1>
            </div>
            <div className="ml-auto flex px-4 md:px-0 md:w-3/4 mx-auto mb-24">
              <div className="w-full mx-auto">
                <div className="grid gap-6">
                  <h2 className="text-4xl text-zinc-700 text-left mt-48">
                    <span className="font-medium">A Smarter Approach to </span>
                    <span className="font-thin italic font-serif leading-none text-sky-600">
                      Writing Essays
                    </span>
                  </h2>
                  <p className="text-lg text-zinc-600 grid gap-2">
                    <span>
                      v0 Report is an advanced AI-powered tool designed to rewrite sentences, making
                      them more concise, and readable, but with original content.{' '}
                    </span>
                    <span>
                      Whether you are a student, a professional, or someone in need of rewriting
                      sentences, our v0 Report's AI sentence rewriter is here to assist you.
                    </span>
                    <span>
                      Using state-of-the-art natural language processing (NLP) algorithms, v0 Report
                      can reword sentences while maintaining their original meaning.
                    </span>
                  </p>
                  <Link
                    className="w-fit h-10 rounded-md px-4 inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-sky-600 text-primary-foreground hover:bg-sky-600/90 "
                    href={'/auth/signup'}
                  >
                    {' '}
                    Get started <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
                <div className="grid gap-6 mt-24">
                  <Image
                    className="rounded-2xl w-full md:w-3/4"
                    src="/images/tools/report.webp"
                    alt="Image"
                    width={500}
                    height={300}
                  />

                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    How to Use v0 Report AI Sentence Rewriter Generator
                  </h2>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Input Your Text</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Simply paste the text you want to rewrite into the provided text box.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Input Your Requirements</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Specify the topic, length, and any particular guidelines or references
                            you need included in your rewriting of your sentences.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Generate Your Essay</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Let our v0 Report work its magic. In just a few seconds, you'll have a
                            complete, high-quality essay ready for review.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Generate Your Sentence</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Let our v0 Report work its magic. In just a few seconds, you'll have a
                            high-quality, and engaging sentence for review.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Review and Edit</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            While v0 Report strives for perfection, we understand the importance of
                            personalization. You can review and make any necessary edits to ensure
                            that the sentences generated meet your standards.
                          </p>{' '}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-6 mt-24">
                  <Image
                    className="rounded-2xl w-full md:w-3/4"
                    src="/images/tools/on-laptop.webp"
                    alt="Image"
                    width={500}
                    height={300}
                  />

                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    Here's how it can help
                  </h2>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">
                            Expert Assistance Across All Academic Disciplines
                          </h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            v0 Report’s AI sentence rewording tool is designed to provide expert
                            assistance across all academic disciplines. Whether you're studying
                            humanities, sciences, social sciences, or any other field, v0 Report has
                            you covered.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Generates a Variety of Sentences</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Our AI sentence rewriter tool is versatile and capable of generating
                            multiple sentence variations for any given text.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Preserves Original Meaning</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            While v0 Report rephrases sentences, it ensures that the sentences are
                            free from grammatical errors and the core message remains intact.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Enhanced Clarity and Readability</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            With v0 Report, you can effortlessly enhance the clarity and readability
                            of your text. It rephrases complex sentences, making them easier to
                            understand and more engaging for your audience.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Time-Saving Efficiency</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Struggling to get started? V0 Report can help you overcome writer's
                            block by providing a solid sentence foundation. You’ll have a structured
                            sentence ready to refine and personalize, making your writing process
                            much smoother.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-6 mt-24">
                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    The AI Sentence Rewriter Tool Preferred by Industry Leaders
                  </h2>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Content Marketers</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            With v0 Report, content marketers can quickly and efficiently rewrite
                            sentences to improve clarity, readability, and SEO performance.
                          </p>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Academic Researchers</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            v0 Report can assist them in rewriting intricate sentences, ensuring
                            their work is easily understandable while maintaining the integrity of
                            their research.
                          </p>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Journalists and Editors</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Journalists and editors need to convey information accurately and
                            engagingly. Editors can also use the tool to polish their writers' work,
                            ensuring the final product is clear and professional.
                          </p>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Corporate Communicators</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            v0 Report helps corporate communicators rewrite and refine their
                            content, whether it's a company memo, press release, or social media
                            post, to ensure it resonates with their audience.
                          </p>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Authors and Creative Writers</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            V0 Report provides various sentence structures and phrasing options,
                            helping writers enhance their storytelling and create more engaging
                            narratives.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-6 mt-24">
                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    Why Choose the v0 AI Report?
                  </h2>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Cutting-Edge Technology</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Our AI sentence rewriter is built on the latest advancements in natural
                            language processing, ensuring high-quality performance and accuracy.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Dedicated Support</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Our team can assist you with any questions or issues, ensuring a smooth
                            and satisfying user experience.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Affordable Plans</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            We offer a range of pricing options to suit different needs and budgets,
                            making our advanced AI technology accessible to everyone.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hidden sm:block">
                <ToolLinks />
              </div>
            </div>
            <div className="w-fit grid gap-8 bg-sky-50 rounded-xl py-8 md:py-20 px-5 md:px-24 mx-2 md:mx-auto">
              <p className="text-2xl font-semibold text-gray-900 ">
                Join the Future of Academic Writing
              </p>
              <p className="text-xl tracking-tight font-medium text-zinc-600 grid gap-2">
                <span>
                  Embrace the efficiency of the automated AI report writing tool with the v0 AI
                  Report Generator.
                </span>
                <span>
                  Sign up now or request a demo to see how our report writer tool can revolutionize
                  how you work with your report data.{' '}
                </span>
                <span>Transform your report analysis with v0 Report's accuracy and speed!</span>
              </p>
            </div>
            <div className="w-11/12 mx-auto my-24 md:w-fit text-center grid gap-8">
              <Link
                rel="canonical"
                href={isSubscribed ? '/home' : '/'}
                className="flex justify-center w-fit mx-auto"
              >
                <Image
                  className="object-scale-down h-8 w-8"
                  src="/images/logo.png"
                  alt="Logo"
                  width={200}
                  height={300}
                />
                <p className="my-auto ml-4 hidden sm:block bg-gray-100 text-gray-600 px-3 py-1.5 rounded ">
                  v0 report
                </p>
              </Link>
              <h2 className="text-3xl tracking-tight font-semibold leading-tight text-gray-900 ">
                Experience the future of document generation today <br />
                with v0 Report!
              </h2>
              <Link
                className="w-fit mx-auto h-8 rounded-md px-4 inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-sky-600 text-primary-foreground hover:bg-sky-600/90 "
                href={'/auth/signup'}
              >
                Get started
              </Link>
            </div>
          </div>
        </main>
      </NonAppNavWrapper>
    </>
  )
}
